import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/** Tab-order focusable elements, excluding disabled and `tabindex="-1"`. */
const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface UseFocusTrapOptions {
    /** Whether the trap is live (the dialog is open). */
    active: boolean;
    /** The dialog panel whose focusables are cycled. */
    containerRef: RefObject<HTMLElement | null>;
    /** Element to focus on open; falls back to the first focusable in the container. */
    initialFocusRef?: RefObject<HTMLElement | null>;
    /** Restore focus to whatever was focused before opening, on close (default true). */
    restoreFocus?: boolean;
    /** Lock `document.body` scroll while active (default false). */
    lockScroll?: boolean;
    /** Handle Escape (e.g. close the dialog). Omit to leave Escape to the caller. */
    onEscape?: () => void;
}

/**
 * Headless modal focus management shared by every dialog (auth modals, FilterModal,
 * the Header drawer): moves focus into the panel on open, cycles Tab / Shift+Tab
 * within it so focus can't reach the page behind, optionally locks body scroll and
 * handles Escape, and restores focus to the trigger on close. Replaces the per-modal
 * hand-rolled copies of the same logic. Focusables are queried live on each keydown,
 * so dynamically shown/hidden controls are always handled.
 */
export function useFocusTrap({
    active,
    containerRef,
    initialFocusRef,
    restoreFocus = true,
    lockScroll = false,
    onEscape,
}: UseFocusTrapOptions): void {
    // Keep the latest onEscape without re-running the effect each render.
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;

    useEffect(() => {
        if (!active || typeof document === 'undefined') return;

        const previouslyFocused = document.activeElement as HTMLElement | null;

        // Initial focus: the requested element, else the first focusable in the panel.
        if (initialFocusRef?.current) {
            initialFocusRef.current.focus();
        } else {
            containerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
        }

        let previousOverflow = '';
        if (lockScroll) {
            previousOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && onEscapeRef.current) {
                onEscapeRef.current();
                return;
            }
            if (event.key !== 'Tab') return;
            const container = containerRef.current;
            if (!container) return;
            const focusables = Array.from(
                container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
            ).filter(el => el.offsetParent !== null);
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (!first || !last) return;
            const activeEl = document.activeElement;
            if (event.shiftKey) {
                if (activeEl === first || !container.contains(activeEl)) {
                    event.preventDefault();
                    last.focus();
                }
            } else if (activeEl === last || !container.contains(activeEl)) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            if (lockScroll) document.body.style.overflow = previousOverflow;
            if (restoreFocus && previouslyFocused?.isConnected) previouslyFocused.focus();
        };
    }, [active, containerRef, initialFocusRef, restoreFocus, lockScroll]);
}
